import { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { TrendingUp } from "lucide-react";
import api from "@/lib/api";

const PALETTE = ["#2E9CFF", "#2BB875", "#FFB340", "#FF6B6B", "#B78CFF", "#4DD0E1"];
const AGG_LABEL = "All training (aggregate)";
const tooltipProps = {
  contentStyle: { background: "#FFFFFF", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, fontFamily: "monospace", fontSize: 12 },
  labelStyle: { color: "#0F172A" },
  itemStyle: { color: "#0F172A" },
};
const axisTick = { fill: "#64748B", fontSize: 11, fontFamily: "monospace" };
const dayLabel = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const PassRateTrends = ({ testid = "pass-trend-chart" }) => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(new Set());
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/training/batch-trends")
      .then((res) => setData(res.data))
      .catch(() => setFailed(true));
  }, []);

  const { rows, lines } = useMemo(() => {
    if (!data) return { rows: [], lines: [] };
    const byDay = {};
    const lines = [];
    const usedNames = new Set();
    (data.series || []).forEach((s, i) => {
      let name = s.exam;
      let n = 2;
      while (usedNames.has(name)) name = `${s.exam} (${n++})`;
      usedNames.add(name);
      lines.push({ key: name, color: PALETTE[i % PALETTE.length], aggregate: false });
      // last point per day keeps the chart readable
      const perDay = {};
      s.points.forEach((p) => { perDay[(p.at || "").slice(0, 10)] = p; });
      Object.entries(perDay).forEach(([day, p]) => {
        byDay[day] = byDay[day] || { day };
        byDay[day][name] = p.pass_rate;
      });
    });
    lines.push({ key: AGG_LABEL, color: "#0F172A", aggregate: true });
    (data.aggregate || []).forEach((a) => {
      byDay[a.day] = byDay[a.day] || { day: a.day };
      byDay[a.day][AGG_LABEL] = a.pass_rate;
    });
    const rows = Object.values(byDay).sort((a, b) => (a.day < b.day ? -1 : 1));
    return { rows, lines };
  }, [data]);

  if (failed) return null;

  const visible = lines.filter((l) => !hidden.has(l.key));
  const pointCount = rows.length;
  const toggle = (key) => {
    const next = new Set(hidden);
    next.has(key) ? next.delete(key) : next.add(key);
    setHidden(next);
  };

  return (
    <div className="technical-card p-6 mb-10" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp size={14} className="text-sky-600" />
        <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">Pass-Rate Trends</p>
      </div>
      <p className="text-[11px] text-muted-foreground mb-4">Pass rate over time per exam, plus an all-training aggregate. A point is recorded on every import and course exam submission.</p>
      {data && lines.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="pass-trend-toggles">
          {lines.map((l, i) => (
            <button
              key={l.key}
              onClick={() => toggle(l.key)}
              data-testid={`pass-trend-chip-${i}`}
              className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors ${hidden.has(l.key) ? "border-slate-200 text-muted-foreground/40" : "border-white/20 text-muted-foreground hover:text-slate-900"}`}
            >
              <span className="h-1.5 w-3 rounded-full" style={{ background: hidden.has(l.key) ? "#CBD5E1" : l.color }} />
              {l.key}
            </button>
          ))}
        </div>
      )}
      {!data ? (
        <div className="h-[220px] flex items-center justify-center border border-dashed border-slate-200 rounded-md">
          <p className="text-xs text-muted-foreground">Loading trends…</p>
        </div>
      ) : pointCount < 2 ? (
        <div className="h-[220px] flex flex-col items-center justify-center border border-dashed border-slate-200 rounded-md" data-testid="pass-trend-empty">
          <TrendingUp size={20} className="text-muted-foreground/40 mb-2" />
          <p className="text-xs text-muted-foreground">Trend needs exam activity on at least two days.</p>
          <p className="mt-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground/60">Imports and course exam submissions add points.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="day" tickFormatter={dayLabel} tick={axisTick} axisLine={{ stroke: "#CBD5E1" }} tickLine={false} />
            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={axisTick} axisLine={false} tickLine={false} width={44} />
            <Tooltip {...tooltipProps} formatter={(v) => [`${v}%`, ""]} labelFormatter={(d) => dayLabel(d)} />
            <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 11 }} />
            {visible.map((l) => (
              <Line
                key={l.key}
                type="monotone"
                dataKey={l.key}
                stroke={l.color}
                strokeWidth={l.aggregate ? 2 : 1.75}
                strokeDasharray={l.aggregate ? "5 4" : undefined}
                dot={{ r: 2.5, fill: l.color, strokeWidth: 0 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
